/**
 * GIESTO — product card gallery.
 *
 * Drives the previous/next arrows on `snippets/giesto-store-card.liquid` and
 * keeps their disabled state in step with the scroll position.
 *
 * This lives in a shared asset rather than inside a section's custom element
 * because more than one section renders the card. It used to sit in
 * `<giesto-store-collection>`, which meant the arrows were dead on the sale
 * page: that page has no such element, so nothing was listening and clicking an
 * arrow did nothing at all — no error, just a control that ignored you.
 *
 * Delegated from the document, so cards added later — an infinite-scroll batch,
 * a cart-drawer recommendation, a section re-render — are covered without any
 * re-binding.
 */
(() => {
  const GALLERY = '[data-gac-gallery]';
  const PREV = '[data-gac-gallery-prev]';
  const NEXT = '[data-gac-gallery-next]';
  const COLOUR_ROW = '.gac-card__colour-groups .csga-row';
  const COLOUR_LIMIT = 3;

  // The snippet is rendered on several page types, and each section that uses
  // it loads this file, so a second execution is possible.
  if (window.__giestoStoreCardGallery) return;
  window.__giestoStoreCardGallery = true;

  /**
   * Swatch Sync normally honours maxVisibleSwatches, but its async re-render
   * and a few older group payloads can still return the entire linked family.
   * Normalise the final DOM so every card has the same three-chip threshold.
   * The extra anchors stay in the row (CSS hides them) so the app can safely
   * re-render or update its active colour without losing product data.
   */
  const normaliseColourRow = (row) => {
    const swatches = [...row.querySelectorAll(':scope > .csw')];
    let more = row.querySelector(':scope > .csga-more');
    const overflow = Math.max(0, swatches.length - COLOUR_LIMIT);

    swatches.forEach((swatch, index) => {
      const hidden = index >= COLOUR_LIMIT;
      swatch.classList.toggle('csga-hide', hidden);
      swatch.toggleAttribute('hidden', hidden);
      if (swatch.getAttribute('aria-hidden') !== String(hidden)) swatch.setAttribute('aria-hidden', String(hidden));
    });

    if (!overflow) {
      more?.remove();
      return;
    }

    if (!more) {
      more = document.createElement('button');
      more.type = 'button';
      more.className = 'csga-more';
      row.append(more);
    }

    const label = `+${overflow}`;
    if (more.textContent !== label) more.textContent = label;
    more.setAttribute('aria-label', label);
  };

  const normaliseColourRows = (root = document) => {
    if (root instanceof Element && root.matches(COLOUR_ROW)) normaliseColourRow(root);
    root.querySelectorAll?.(COLOUR_ROW).forEach(normaliseColourRow);
  };

  window.GiestoStoreCards = Object.assign(window.GiestoStoreCards || {}, { normaliseColourRows });

  normaliseColourRows();
  const dirtyRows = new Set();
  let colourFrame = 0;
  const queueRows = root => {
    const row = root.closest?.(COLOUR_ROW);
    if (row) dirtyRows.add(row);
    if (root.matches?.(COLOUR_ROW)) dirtyRows.add(root);
    for (const child of root.querySelectorAll?.(COLOUR_ROW) || []) dirtyRows.add(child);
    if (dirtyRows.size && !colourFrame) colourFrame = requestAnimationFrame(() => {
      colourFrame = 0;
      for (const row of dirtyRows) if (row.isConnected) normaliseColourRow(row);
      dirtyRows.clear();
    });
  };
  new MutationObserver(records => {
    for (const record of records) {
      if (record.target.closest?.(COLOUR_ROW)) queueRows(record.target);
      for (const node of record.addedNodes) if (node.nodeType === 1) queueRows(node);
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  const mountGallery = gallery => {
    const template = gallery?.querySelector('template[data-gac-gallery-frames]');
    if (!template) return;
    gallery.append(template.content);
    template.remove();
    syncArrows(gallery);
  };
  for (const type of ['pointerover', 'pointerdown', 'focusin']) {
    document.addEventListener(type, event => {
      const card = event.target.closest?.('.gac-card');
      const gallery = card?.querySelector(GALLERY);
      if (gallery) mountGallery(gallery);
    }, { passive: true });
  }

  /** @param {Element} gallery */
  const syncArrows = (gallery) => {
    const card = gallery.closest('.gac-card');
    if (!card) return;

    // A fractional scrollLeft is normal mid-snap; a pixel of tolerance keeps the
    // ends from reading as "not quite there".
    const atStart = gallery.scrollLeft <= 1;
    const atEnd = !gallery.querySelector('template[data-gac-gallery-frames]') && gallery.scrollLeft >= gallery.scrollWidth - gallery.clientWidth - 1;

    card.querySelector(PREV)?.toggleAttribute('disabled', atStart);
    card.querySelector(NEXT)?.toggleAttribute('disabled', atEnd);
  };

  window.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const moreColours = target.closest('.gac-card__colour-groups .csga-more');
    if (!moreColours) return;

    // The +N control is Swatch Sync's, not the reference's — the reference has
    // no such chip on a card at all. By request it behaves like the rest of the
    // card and opens the product, where every colour is listed, rather than
    // expanding the remaining swatches in place.
    // Capture phase is intentional: it runs before the app's own listener,
    // which would otherwise expand the row.
    event.preventDefault();
    event.stopImmediatePropagation();

    const href = moreColours.closest('.gac-card')?.querySelector('a[href*="/products/"]')?.href;
    if (href) window.location.assign(href);
  }, { capture: true });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const prev = target.closest(PREV);
    const next = target.closest(NEXT);
    if (!prev && !next) return;

    // The arrows sit inside the card's product link.
    event.preventDefault();

    const gallery = target.closest('.gac-card')?.querySelector(GALLERY);
    if (!(gallery instanceof HTMLElement)) return;

    mountGallery(gallery);
    const amount = next ? gallery.clientWidth : -gallery.clientWidth;
    const before = gallery.scrollLeft;
    gallery.scrollBy({ left: amount, behavior: 'smooth' });

    // A smooth programmatic scroll can be dropped outright on a mandatory-snap
    // scroller — measured on this gallery: scrollLeft stayed at 0 for both
    // scrollBy and scrollTo with `behavior: 'smooth'`, with and without the snap
    // temporarily disabled, while the same call at the default behaviour moved
    // it the full width. Rather than leave the arrow looking dead when that
    // happens, check a beat later and jump. Where the animation does run this
    // sees the scroller already moving and does nothing.
    setTimeout(() => {
      if (Math.abs(gallery.scrollLeft - before) < 1) gallery.scrollBy({ left: amount });
    }, 250);
  });

  // Scroll does not bubble, but a capture-phase listener on the document still
  // sees it, so one listener covers every gallery on the page.
  document.addEventListener(
    'scroll',
    (event) => {
      const gallery = event.target;
      if (gallery instanceof HTMLElement && gallery.matches(GALLERY)) syncArrows(gallery);
    },
    { capture: true, passive: true }
  );

  // The app scans .csga-pc eagerly. Expose only visible/nearby card
  // placeholders to it, including cards arriving from pagination or drawers.
  const pendingColors = new WeakSet();
  const observedColors = new Set();
  const colorObserver = new IntersectionObserver(entries => {
    let activated = false;
    for (const entry of entries) if (entry.isIntersecting) {
      entry.target.classList.replace('gac-color-pending', 'csga-pc');
      colorObserver.unobserve(entry.target);
      observedColors.delete(entry.target);
      activated = true;
    }
    if (activated) window.CSGA?.PC?.scan?.();
  }, { rootMargin: '300px' });
  const discoverColors = (root = document) => {
    for (const target of observedColors) if (!target.isConnected) { colorObserver.unobserve(target); observedColors.delete(target); }
    const selector = '.gac-color-pending[data-gac-swatch-sync]';
    const containers = [...root.querySelectorAll?.(selector) || []];
    if (root.matches?.(selector)) containers.unshift(root);
    for (const container of containers) {
      if (pendingColors.has(container)) continue;
      pendingColors.add(container);
      observedColors.add(container);
      colorObserver.observe(container);
    }
  };
  new MutationObserver(records => {
    discoverColors(document.createDocumentFragment());
    for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) discoverColors(node);
  }).observe(document.documentElement, { childList: true, subtree: true });
  discoverColors();

  // Frame videos behave like moving photos: muted, no controls, playing only
  // while at least half on screen. Reduced motion and data saver keep them on
  // their poster frame.
  const videos = new Map();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const still = () => motion.matches || Boolean(navigator.connection?.saveData);
  const release = video => {
    video.pause();
    if (video.hasAttribute('data-gac-video-unloaded') || (!video.currentSrc && video.readyState === 0)) return;
    // Store sources on the elements: editorial view clones grid cards, and
    // those clones must be able to restore a previously unloaded video too.
    video.setAttribute('data-gac-video-unloaded', '');
    if (video.getAttribute('src')) video.dataset.gacVideoSrc = video.getAttribute('src');
    video.removeAttribute('src');
    for (const source of video.querySelectorAll('source[src]')) {
      source.dataset.gacVideoSrc = source.getAttribute('src');
      source.removeAttribute('src');
    }
    // Pausing alone retains every visited card's media buffer in a long list.
    // Reset the resource selection to release it, keeping the poster visible.
    video.load();
  };
  const failedSources = new WeakMap();
  const selectSource = video => {
    if (!video.dataset.gacVideoSources) return;
    let sources; try { sources = JSON.parse(video.dataset.gacVideoSources); } catch { return; }
    const { width, height } = video.getBoundingClientRect();
    if (!width || !height) return;
    const dpr = Math.min(devicePixelRatio || 1, 3);
    // Both axes must cover the actual object-fit box, including a taller crop.
    const covers = source => source.width >= width * dpr && source.height >= height * dpr;
    sources = sources.filter(source => !failedSources.get(video)?.has(new URL(source.url, location.href).href) && (!source.giestoDerivative || (innerWidth <= 749 && covers(source))));
    const candidates = sources.filter(source => source.type === 'video/mp4' && source.width > 0 && video.canPlayType(source.type)).sort((a, b) => a.width - b.width);
    const selected = candidates.find(covers) || candidates.at(-1) || sources.find(source => video.canPlayType(source.type));
    if (selected && video.getAttribute('src') !== selected.url) {
      const resumeTime = video.currentTime;
      if (resumeTime > 0) video.addEventListener('loadedmetadata', () => { if (video.getAttribute('src') === selected.url && Number.isFinite(video.duration)) video.currentTime = Math.min(resumeTime, Math.max(0, video.duration - .01)); }, { once: true });
      video.src = selected.url;
      video.dataset.gacVideoSrc = selected.url;
      video.removeAttribute('data-gac-video-unloaded');
      video.load();
    }
  };
  const restore = video => {
    if (!video.hasAttribute('data-gac-video-unloaded')) return;
    video.removeAttribute('data-gac-video-unloaded');
    if (video.dataset.gacVideoSrc) {
      video.setAttribute('src', video.dataset.gacVideoSrc);
      delete video.dataset.gacVideoSrc;
    }
    for (const source of video.querySelectorAll('source[data-gac-video-src]')) {
      source.setAttribute('src', source.dataset.gacVideoSrc);
      delete source.dataset.gacVideoSrc;
    }
    video.load();
  };
  const sizePoster = video => {
    const poster = video.getAttribute('poster');
    const renderedWidth = video.getBoundingClientRect().width;
    if (!poster || !renderedWidth) return;
    const width = Math.min(1600, Math.max(400, Math.ceil(renderedWidth * (devicePixelRatio || 1) / 100) * 100));
    const posterURL = new URL(poster, location.origin);
    if (posterURL.searchParams.has('width')) posterURL.searchParams.set('width', width);
    const responsive = posterURL.searchParams.has('width') ? posterURL.href : poster.replace(/_\d+x(?=\.)/, `_${width}x`);
    if (responsive === poster) return;
    video.poster = responsive;
    video.querySelector('img')?.setAttribute('src', responsive);
  };
  const update = (video, visible) => {
    if (visible && !document.hidden && !still()) {
      sizePoster(video);
      selectSource(video);
      restore(video);
      video.muted = true;
      video.play()?.catch(() => {});
    } else video.pause();
  };
  const observer = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!videos.has(entry.target)) continue;
      const visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
      videos.set(entry.target, visible);
      update(entry.target, visible);
    }
  }, { threshold: [0, 0.5] });
  // Keep nearby paused videos warm, but don't accumulate buffers for the
  // entire sale catalog. This also covers dynamically mounted editorial cards.
  const rangeObserver = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (videos.has(entry.target) && !entry.isIntersecting) release(entry.target);
    }
  }, { rootMargin: '1000px' });
  const discover = (root = document) => {
    for (const [video] of videos) {
      if (video.isConnected) continue;
      release(video);
      observer.unobserve(video);
      rangeObserver.unobserve(video);
      videos.delete(video);
    }
    const found = [...root.querySelectorAll?.('video.gac-frame-video') || []];
    if (root.matches?.('video.gac-frame-video')) found.unshift(root);
    for (const video of found) {
      if (videos.has(video)) continue;
      video.playsInline = true;
      if (video.dataset.gacVideoSources) video.addEventListener('error', () => {
        if (!video.currentSrc) return;
        if (!failedSources.has(video)) failedSources.set(video, new Set());
        failedSources.get(video).add(video.currentSrc);
        if (videos.get(video)) { selectSource(video); video.play()?.catch(() => {}); }
      });
      videos.set(video, false);
      observer.observe(video);
      rangeObserver.observe(video);
    }
  };
  let discoveryPending = false;
  const discoveryRoots = new Set();
  new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) discoveryRoots.add(node);
    if (discoveryPending) return;
    discoveryPending = true;
    requestAnimationFrame(() => {
      discoveryPending = false;
      // A detached root only performs disconnected-buffer cleanup.
      if (!discoveryRoots.size) discover(document.createDocumentFragment());
      for (const root of discoveryRoots) if (root.isConnected) discover(root);
      discoveryRoots.clear();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
  const refresh = () => { for (const [video, visible] of videos) update(video, visible); };
  document.addEventListener('visibilitychange', refresh);
  motion.addEventListener('change', refresh);
  let resizePending = false;
  window.addEventListener('resize', () => { if (resizePending) return; resizePending = true; requestAnimationFrame(() => { resizePending = false; refresh(); }); }, { passive: true });
  discover();
})();
